import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useField, useFormikContext, getIn } from 'formik';
import { Form, ProgressBar, InputGroup, Button, OverlayTrigger } from 'react-bootstrap';
import {
  FormCol,
  FieldLabel,
  FieldTooltip,
  promiseModalDialog,
  toastSuccess,
  toastFailure,
} from 'motion-components';
import { FaFileUpload, FaTrashAlt } from 'react-icons/fa';
import gql from 'graphql-tag';
import { uploadFile2S3withSignedUrl } from 'shared-pages/common/s3helpfunc';
import { useTranslation } from 'react-i18next';

const gqlAddFilesRepository = gql`
  mutation addFilesrepository($fileName: String!, $mimeMediaType: String!) {
    addFilesrepository(fileName: $fileName, mimeMediaType: $mimeMediaType) {
      id
      url
    }
  }
`;

const gqlGetFilesRepositoryFileUrlById = gql`
  query getFilesrepositoryFileUrlById($id: Int) {
    getFilesrepositoryFileUrlById(id: $id)
  }
`;

const gqlDeleteFilesRepository = gql`
  mutation deleteFilesrepository($id: Int!) {
    deleteFilesrepository(id: $id)
  }
`;

type Props = {
  as?: React.ElementType<any>;
  style?: any;
  label: string | React.ReactNode;
  helpText?: string;
  fileIdFieldName: string;
  fileRec: any;
  acceptFiles?: string | null;
  canEdit?: boolean;
  /** Αν true, καλεί submitForm() μετά την επιτυχή μεταφόρτωση. Default: false. */
  submitOnUpload?: boolean;
  /** Προαιρετικό Formik path για το όνομα αρχείου (ώστε να είναι διαθέσιμο και σε γονικά components). */
  fileNameFieldName?: string;
};

// fileIdFieldName => fileId fieldName i.e. signFileId
export const FileRepoField = ({
  label,
  helpText,
  fileIdFieldName,
  fileRec,
  as = FormCol,
  acceptFiles = null,
  style = {},
  canEdit = true,
  submitOnUpload = false,
  fileNameFieldName,
}: Props) => {
  const { t } = useTranslation();
  const fileInput = useRef(null);
  // const { handleGraphqlError } = useHandleError('FileRepoField');
  const [{ value }, { touched, error, initialValue }] = useField<string>(fileIdFieldName);
  const { values, setFieldValue, setFieldTouched, submitForm, submitCount } = useFormikContext<any>();

  const [uploadStatus, setUploadStatus] = useState(-1);
  /** Όνομα αρχείου μετά upload, πριν την αποθήκευση φόρμας (όταν δεν υπάρχει fileNameFieldName). */
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const storedFileName =
    fileNameFieldName != null ? (getIn(values, fileNameFieldName) as string | null) : null;
  const displayFileName =
    (fileRec?.fileName || storedFileName || pendingFileName || '').trim();

  useEffect(() => {
    if (fileRec?.id != null && fileRec.id === value) {
      setPendingFileName(null);
      if (fileNameFieldName && fileRec?.fileName) {
        setFieldValue(fileNameFieldName, fileRec.fileName, false);
      }
    }
  }, [fileNameFieldName, fileRec?.fileName, fileRec?.id, setFieldValue, value]);

  const client = useApolloClient();
  const [addFile] = useMutation(gqlAddFilesRepository);
  const [deleteFile] = useMutation(gqlDeleteFilesRepository);
  // const [getFileUrl, { data: dataFileUrl }] = useLazyQuery(gqlGetFilesRepositoryFileUrlById);

  /**
  // * ----- HANDLER: Upload File
  */
  const handleFileSelect = async (fileObj: File) => {
    console.log(fileObj);
    // Εχει επιλεγεί αρχείο...
    const res = await addFile({
      // Mutation
      variables: { fileName: fileObj.name, mimeMediaType: fileObj.type },
    });
    const { id, url } = res?.data?.addFilesrepository;
    console.log(id, url);
    if (id && url) {
      setUploadStatus(0);
      uploadFile2S3withSignedUrl(
        fileObj,
        url,
        () => {
          toastSuccess(t('File Uploaded Succesfully')); //'To Aρχείο Μεταφέρθηκε στον server');
          setFieldValue(fileIdFieldName, id);
          setFieldTouched(fileIdFieldName, true);
          if (fileNameFieldName) {
            setFieldValue(fileNameFieldName, fileObj.name, false);
          } else {
            setPendingFileName(fileObj.name);
          }
          setUploadStatus(-1);
          if (submitOnUpload) submitForm();
        }, //onFileUploaded
        () => {
          toastFailure(t('Failed to upload file')); //('Παρουσιάστηκε Κάποιο Πρόβλημα στο Ανέβασμα του Αρχείου');
          setUploadStatus(-1);
        }, //onUploadError
        (percent) => {
          setUploadStatus(percent);
        }, //onUploadProgress
      );
    }
  };

  /*
      Delete File
  */
  const handleFileDelete = useCallback(async () => {
    if (!canEdit) return;
    const res = await promiseModalDialog({
      title: t('Delete File Record'), //'Διαγραφή Εγγραφής Αρχείου',
      text: t('Do you really want to delete the file;'), //'Είστε σίγουροι ότι θέλετε να διαγράψετε το Αρχείο;',
    });
    if (!res) return;
    setFieldValue(fileIdFieldName, null);
    setFieldTouched(fileIdFieldName, true);
    if (fileNameFieldName) {
      setFieldValue(fileNameFieldName, null, false);
    } else {
      setPendingFileName(null);
    }
    if (submitOnUpload) await submitForm();

    // await deleteFile({ variables: { id: value } }).catch(error => {
    //   handleGraphqlError('mutation-delete', error);
    // });
    setTimeout(async () => {
      await deleteFile({ variables: { id: value } }).catch((error) => {});
      //toastSuccess('Το Αρχείο Διαγράφηκε');
    }, 1000);
  }, [
    deleteFile,
    fileIdFieldName,
    fileNameFieldName,
    setFieldTouched,
    setFieldValue,
    submitForm,
    submitOnUpload,
    t,
    value,
  ]);

  /**
  // * ----- HANDLER: Open File (κανω το query με promise)
  */
  const handleFileOpen = async () => {
    console.log('***OPEN FILE***');
    client
      .query({
        query: gqlGetFilesRepositoryFileUrlById,
        variables: { id: value },
      })
      .then((res) => {
        const url = res?.data?.getFilesrepositoryFileUrlById;
        if (url) {
          window.open(url);
        } else {
          toastFailure(t('Error during file download')); //'Πρόβλημα Στο Κατέβασμα του Αρχείου'
        }
      })
      .catch((err) => toastFailure(t('Error during file download ') + err)); //`Πρόβλημα στο κατέβασμα του αρχείου err:${err}`));
  };

  return (
    <Form.Group
      className="pb-2"
      as={as}
      style={{ paddingRight: 'unset', paddingLeft: 'unset', marginBottom: '3px' }}
    >
      <FieldLabel text={label} helpText={helpText} />
      {uploadStatus !== -1 ? (
        <ProgressBar className="my-2" now={uploadStatus} />
      ) : (
        <OverlayTrigger
          delay={{ show: 1000, hide: 200 }}
          overlay={
            <FieldTooltip
              text={fileIdFieldName}
              value={
                value != null ? displayFileName : pendingFileName || storedFileName || ''
              }
            />
          }
          placement="top"
        >
          <InputGroup>
            <Form.Control
              disabled={true}
              value={
                value != null ? displayFileName : pendingFileName || storedFileName || ''
              }
              name={fileRec?.fileName}
              type={'text'}
            />

          {(value === null || value === undefined) && (
            <>
              {/* <FilePicker
                onChange={fileObject => handleFileSelect(fileObject)}
                onError={errMsg => console.log(errMsg)}
                //extensions={fileExtentions}
                extensions={['jpg', 'jpeg', 'png']}
              >
                <Button className={'file-input'} variant="success" onClick={() => console.log('Hi')}>
                  <FaFileUpload />
                </Button>
              </FilePicker> */}
              <Button
                className={'file-input'}
                variant="success"
                onClick={() => {
                  fileInput.current.click();
                }}
                disabled={!canEdit}
                style={{
                  height: '22px',
                  padding: '0 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaFileUpload style={{ fontSize: '12px' }} />
                <input
                  hidden
                  type="file"
                  onChange={(e) => handleFileSelect(e?.target?.files[0])}
                  accept={acceptFiles}
                  ref={fileInput}
                />
              </Button>
            </>
          )}

          {value && value !== null && (
            <>
              <Button
                className={'file-input'}
                onClick={() => handleFileOpen()}
                style={{
                  height: '22px',
                  padding: '0 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                }}
              >
                {t('Άνοιγμα Αρχείου')}
              </Button>
              {canEdit && (
                <Button
                  className={'file-input'}
                  variant="danger"
                  onClick={() => handleFileDelete()}
                  style={{
                    height: '22px',
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FaTrashAlt style={{ fontSize: '12px' }} />
                </Button>
              )}
            </>
          )}
          {(touched || submitCount > 0) && (
            <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
          )}
          </InputGroup>
        </OverlayTrigger>
      )}
    </Form.Group>
  );
};
